import {
  FcColor,
  FcColorType,
  FcPercentageSize,
  FcPositionElement,
  FcShape,
  FcSize,
  FcText,
  fcBuildColorStringFromAttribs,
  fcBuildSvgColor,
  fcBuildSvgColorAttribs,
  fcBuildSvgColorForImageFill
} from '@dtx-company/flowcode-generator-types/src'
import { fcGenerateGradient } from './fc_gradient'
import { fcGetDomId } from './fc_dom'
import { fcGetFont } from './fc_fonts'
import { fcImageAddSize, fcImageToTag } from './fc_images'
import { roundDecimal } from './fc_utils'

interface FcTextData {
  data: string
  height: number
}
export async function fcPositionElementRenderer(
  positionElement: FcPositionElement,
  x: number,
  y: number,
  width: FcSize,
  useCmyk: boolean
): Promise<string> {
  const positionElementSVG = new FcPositionElementSVG(positionElement, x, y, width, useCmyk)
  await positionElementSVG.render()
  return positionElementSVG.svg
}

class FcPositionElementSVG {
  public svgDOM = ''
  private readonly size: number
  private positionElement: FcPositionElement
  private idBase: string
  private useCmyk: boolean
  private x: number
  private y: number

  constructor(
    positionElement: FcPositionElement,
    x: number,
    y: number,
    size: FcSize,
    useCmyk: boolean
  ) {
    this.positionElement = positionElement
    this.size = size
    this.x = x
    this.y = y
    this.idBase = fcGetDomId()
    this.useCmyk = useCmyk
  }

  get svg(): string {
    return this.svgDOM
  }

  async render(): Promise<void> {
    let outerShapeColor = this.positionElement.outerShapeColor
    let innerShapeColor = this.positionElement.innerShapeColor
    let textColor: FcColor | undefined = undefined
    if (this.positionElement.gradient) {
      const label = 'eyeGradient' + this.idBase + this.positionElement.position.property
      const gradientDef = fcGenerateGradient(this.useCmyk, this.positionElement.gradient, label)
      this.svgDOM += gradientDef
      outerShapeColor = `url(#${label})`
      innerShapeColor = outerShapeColor
      textColor = outerShapeColor
    }

    if (this.positionElement.imageUrl) {
      const imageData = await fcImageToTag(
        this.useCmyk,
        this.positionElement.imageUrl,
        this.positionElement.fillSvg
          ? fcBuildSvgColorForImageFill(outerShapeColor, this.useCmyk)
          : undefined
      )
      this.applyCustomImage(imageData)
      return
    }

    await this.applyOuterShape(
      this.useCmyk,
      this.positionElement.outerShape,
      outerShapeColor,
      (this.size * this.positionElement.outerShapeWidth) / 100,
      this.positionElement.backgroundColor,
      this.positionElement.text,
      textColor,
      this.positionElement.outerShapeOutlineColor,
      this.positionElement.outerShapeOutlineSize
    )

    const innerShapeWidth = (this.size * this.positionElement.innerShapeWidth) / 100
    if (this.positionElement.innerImageUrl) {
      const imageData = await fcImageToTag(
        this.useCmyk,
        this.positionElement.innerImageUrl,
        this.positionElement.fillSvg
          ? fcBuildSvgColorForImageFill(innerShapeColor, this.useCmyk)
          : undefined
      )
      this.applyCustomImageInner(imageData, innerShapeWidth)
    } else {
      this.applyInnerShape(
        this.useCmyk,
        this.positionElement.innerShape,
        innerShapeColor,
        innerShapeWidth,
        this.positionElement.innerShapeOutlineColor,
        this.positionElement.innerShapeOutlineSize
      )
    }
  }

  async applyOuterShape(
    useCmyk: boolean,
    outerShape: FcShape,
    outerShapeColor: FcColor,
    outerShapeWidth: number,
    backgroundColor: FcColor,
    text?: FcText,
    textColor?: FcColor,
    outlineColor?: FcColor,
    outlineSize?: FcPercentageSize
  ): Promise<void> {
    let element = ''
    let offset = 0
    let textSpace = Math.round(this.size * 5) / 100
    if (textSpace < 2) {
      textSpace = 2
    } else if (textSpace > 3) {
      textSpace = 3
    }
    switch (outerShape) {
      case FcShape.LOGO:
        const textData = await this.applyText(useCmyk, text!, textColor)
        const h = textData.height + textSpace
        element = `<rect x="${this.x}" y="${this.y}" width='${this.size}' height='${
          this.size
        }' shape-rendering='crispEdges' ${fcBuildSvgColor(
          backgroundColor,
          FcColorType.FILL,
          useCmyk
        )}/>`
        if (outlineColor && outlineSize) {
          element += `<polygon
                       points="${roundDecimal(this.x)},${roundDecimal(this.y)}
                               ${roundDecimal(this.x + this.size)},${roundDecimal(this.y)}
                               ${roundDecimal(this.x + this.size)},${roundDecimal(
            this.y + this.size - h
          )}
                               ${roundDecimal(this.x + this.size - outerShapeWidth)},${roundDecimal(
            this.y + this.size - h
          )}
                               ${roundDecimal(this.x + this.size - outerShapeWidth)},${roundDecimal(
            this.y + outerShapeWidth
          )}
                               ${roundDecimal(this.x + outerShapeWidth)},${roundDecimal(
            this.y + outerShapeWidth
          )}
                               ${roundDecimal(this.x + outerShapeWidth)},${roundDecimal(
            this.y + this.size - h
          )}
                               ${roundDecimal(this.x)},${roundDecimal(this.y + this.size - h)}
                            " shape-rendering='crispEdges' ${fcBuildSvgColor(
                              outlineColor,
                              FcColorType.FILL,
                              useCmyk
                            )}/>`
          offset = outlineSize
        }
        element += `<polygon
                       points="${roundDecimal(this.x + offset)},${roundDecimal(this.y + offset)}
                               ${roundDecimal(this.x + this.size - offset)},${roundDecimal(
          this.y + offset
        )}
                               ${roundDecimal(this.x + this.size - offset)},${roundDecimal(
          this.y + this.size - h - offset
        )}
                               ${roundDecimal(
                                 this.x + this.size - outerShapeWidth + offset
                               )},${roundDecimal(this.y + this.size - h - offset)}
                               ${roundDecimal(
                                 this.x + this.size - outerShapeWidth + offset
                               )},${roundDecimal(this.y + outerShapeWidth - offset)}
                               ${roundDecimal(this.x + outerShapeWidth - offset)},${roundDecimal(
          this.y + outerShapeWidth - offset
        )}
                               ${roundDecimal(this.x + outerShapeWidth - offset)},${roundDecimal(
          this.y + this.size - h - offset
        )}
                               ${roundDecimal(this.x + offset)},${roundDecimal(
          this.y + this.size - h - offset
        )}
                            " shape-rendering='crispEdges' ${fcBuildSvgColor(
                              outerShapeColor,
                              FcColorType.FILL,
                              useCmyk
                            )}/>`
        element += textData.data
        break
      case FcShape.SQUARE:
        element = `<rect x="${this.x}" y="${this.y}" width='${this.size}' height='${
          this.size
        }' shape-rendering='crispEdges' ${fcBuildSvgColor(
          backgroundColor,
          FcColorType.FILL,
          useCmyk
        )}/>`
        if (outlineColor && outlineSize) {
          element += `<polygon
                       points="${roundDecimal(this.x)},${roundDecimal(this.y)}
                               ${roundDecimal(this.x + this.size)},${roundDecimal(this.y)}
                               ${roundDecimal(this.x + this.size)},${roundDecimal(
            this.y + this.size - outerShapeWidth
          )}
                               ${roundDecimal(this.x + this.size - outerShapeWidth)},${roundDecimal(
            this.y + this.size - outerShapeWidth
          )}
                               ${roundDecimal(this.x + this.size - outerShapeWidth)},${roundDecimal(
            this.y + outerShapeWidth
          )}
                               ${roundDecimal(this.x + outerShapeWidth)},${roundDecimal(
            this.y + outerShapeWidth
          )}
                               ${roundDecimal(this.x + outerShapeWidth)},${roundDecimal(
            this.y + this.size - outerShapeWidth
          )}
                               ${roundDecimal(this.x + this.size)},${roundDecimal(
            this.y + this.size - outerShapeWidth
          )}
                               ${roundDecimal(this.x + this.size)},${roundDecimal(
            this.y + this.size
          )}
                               ${roundDecimal(this.x)},${roundDecimal(this.y + this.size)}
                            " shape-rendering='crispEdges' ${fcBuildSvgColor(
                              outlineColor,
                              FcColorType.FILL,
                              useCmyk
                            )}/>`
          offset = outlineSize
        }
        element += `<polygon
                       points="${roundDecimal(this.x + offset)},${roundDecimal(this.y + offset)}
                               ${roundDecimal(this.x + this.size - offset)},${roundDecimal(
          this.y + offset
        )}
                               ${roundDecimal(this.x + this.size - offset)},${roundDecimal(
          this.y + this.size - offset
        )}
                               ${roundDecimal(
                                 this.x + this.size - outerShapeWidth + offset
                               )},${roundDecimal(this.y + this.size - offset)}
                               ${roundDecimal(
                                 this.x + this.size - outerShapeWidth + offset
                               )},${roundDecimal(this.y + outerShapeWidth - offset)}
                               ${roundDecimal(this.x + outerShapeWidth - offset)},${roundDecimal(
          this.y + outerShapeWidth - offset
        )}
                               ${roundDecimal(this.x + outerShapeWidth - offset)},${roundDecimal(
          this.y + this.size - outerShapeWidth + offset
        )}
                               ${roundDecimal(this.x + this.size - offset)},${roundDecimal(
          this.y + this.size - outerShapeWidth + offset
        )}
                               ${roundDecimal(this.x + this.size - offset)},${roundDecimal(
          this.y + this.size - offset
        )}
                               ${roundDecimal(this.x + offset)},${roundDecimal(
          this.y + this.size - offset
        )}
                            " shape-rendering='crispEdges' ${fcBuildSvgColor(
                              outerShapeColor,
                              FcColorType.FILL,
                              useCmyk
                            )}/>`
        break
      default:
        element = `<circle cx="${roundDecimal(this.x + this.size / 2)}" cy="${roundDecimal(
          this.y + this.size / 2
        )}" r="${roundDecimal(
          this.size / 2 - outerShapeWidth / 2
        )}" shape-rendering='geometricPrecision' ${fcBuildSvgColor(
          outerShapeColor,
          FcColorType.STROKE,
          useCmyk
        )} stroke-width="${outerShapeWidth}" ${fcBuildSvgColor(
          backgroundColor,
          FcColorType.FILL,
          useCmyk
        )}/>`
        if (outlineColor && outlineSize) {
          element += `<circle cx="${roundDecimal(this.x + this.size / 2)}" cy="${roundDecimal(
            this.y + this.size / 2
          )}" r="${roundDecimal(
            this.size / 2 - outlineSize / 2
          )}" shape-rendering='geometricPrecision' ${fcBuildSvgColor(
            outlineColor,
            FcColorType.STROKE,
            useCmyk
          )} stroke-width="${outlineSize}" fill="none"/>`
          element += `<circle cx="${roundDecimal(this.x + this.size / 2)}" cy="${roundDecimal(
            this.y + this.size / 2
          )}" r="${roundDecimal(
            this.size / 2 - outerShapeWidth + outlineSize / 2
          )}" shape-rendering='geometricPrecision' ${fcBuildSvgColor(
            outlineColor,
            FcColorType.STROKE,
            useCmyk
          )} stroke-width="${outlineSize}" fill="none"/>`
        }
        break
    }
    this.svgDOM += element
  }

  applyInnerShape(
    useCmyk: boolean,
    innerShape: FcShape,
    innerShapeColor: FcColor,
    innerShapeWidth: FcPercentageSize,
    outlineColor?: FcColor,
    outlineSize?: FcPercentageSize
  ): void {
    let element = ''
    switch (innerShape) {
      case FcShape.DIAMOND:
        const diamondWidth = innerShapeWidth / Math.sqrt(2)
        element = `<rect x='${roundDecimal(
          this.x + this.size / 2 - diamondWidth / 2
        )}' y='${roundDecimal(
          this.y + this.size / 2 - diamondWidth / 2
        )}' shape-rendering='crispEdges' width='${diamondWidth}' height='${diamondWidth}' ${fcBuildSvgColor(
          innerShapeColor,
          FcColorType.FILL,
          useCmyk
        )} transform='rotate(45 ${this.x + this.size / 2} ${this.y + this.size / 2})'/>`
        if (outlineColor && outlineSize) {
          element += `<rect x='${roundDecimal(
            this.x + this.size / 2 - diamondWidth / 2 + outlineSize / 2
          )}' y='${roundDecimal(
            this.y + this.size / 2 - diamondWidth / 2 + outlineSize / 2
          )}' shape-rendering='crispEdges' width='${diamondWidth - outlineSize}' height='${
            diamondWidth - outlineSize
          }' fill='none' ${fcBuildSvgColor(
            outlineColor,
            FcColorType.STROKE,
            useCmyk
          )} stroke-width='${outlineSize}' transform='rotate(45 ${roundDecimal(
            this.x + this.size / 2
          )} ${roundDecimal(this.y + this.size / 2)})'/>`
        }
        break
      case FcShape.CIRCLE:
        element = `<circle cx="${roundDecimal(this.x + this.size / 2)}" cy="${roundDecimal(
          this.y + this.size / 2
        )}" r="${roundDecimal(innerShapeWidth)}" ${fcBuildSvgColor(
          innerShapeColor,
          FcColorType.FILL,
          useCmyk
        )} />`
        if (outlineColor && outlineSize) {
          element += `<circle cx="${roundDecimal(this.x + this.size / 2)}" cy="${roundDecimal(
            this.y + this.size / 2
          )}" r="${roundDecimal(innerShapeWidth - outlineSize / 2)}" fill="none" ${fcBuildSvgColor(
            outlineColor,
            FcColorType.STROKE,
            useCmyk
          )} stroke-width='${outlineSize}'/>`
        }
        break
      default:
        // Square is default
        element = `<rect x='${roundDecimal(
          this.x + this.size / 2 - innerShapeWidth / 2
        )}' y='${roundDecimal(this.y + this.size / 2 - innerShapeWidth / 2)}' width='${roundDecimal(
          innerShapeWidth
        )}' shape-rendering='crispEdges' height='${innerShapeWidth}' ${fcBuildSvgColor(
          innerShapeColor,
          FcColorType.FILL,
          useCmyk
        )} />`
        if (outlineColor && outlineSize) {
          element += `<rect x='${roundDecimal(
            this.x + this.size / 2 - innerShapeWidth / 2 + outlineSize / 2
          )}' y='${roundDecimal(
            this.y + this.size / 2 - innerShapeWidth / 2 + outlineSize / 2
          )}' width='${roundDecimal(
            innerShapeWidth - outlineSize
          )}' shape-rendering='crispEdges' height='${roundDecimal(
            innerShapeWidth - outlineSize
          )}' fill='none' ${fcBuildSvgColor(
            outlineColor,
            FcColorType.STROKE,
            useCmyk
          )} stroke-width='${outlineSize}'/>`
        }
    }
    this.svgDOM += element
  }

  async applyText(useCmyk: boolean, text: FcText, color?: FcColor): Promise<FcTextData> {
    const textToSVG = await fcGetFont(text)
    if (!color) {
      color = text.fontColor
    }

    const colorAttribs = fcBuildSvgColorAttribs(useCmyk, color)
    const colorStr = fcBuildColorStringFromAttribs(colorAttribs)
    const attributes: any = { fill: colorStr, stroke: colorStr }

    // so it looks the same as when we used to scale down from large version
    attributes['stroke-width'] = 1 / 5

    if (colorAttribs.opacity) {
      attributes['fill-opacity'] = colorAttribs.opacity
      attributes['stroke-opacity'] = colorAttribs.opacity
    }
    if (text.fontOutlineColor && text.fontOutlineSize) {
      const outlineColorAttribs = fcBuildSvgColorAttribs(useCmyk, text.fontOutlineColor)
      const outlineColorStr = fcBuildColorStringFromAttribs(outlineColorAttribs)
      attributes['stroke'] = outlineColorStr
      attributes['stroke-width'] = text.fontOutlineSize
    }
    const options = {
      x: 0,
      y: 0,
      fontSize: text.fontSize,
      anchor: 'top',
      attributes: attributes,
      letterSpacing: 0.05
    }
    const pathData = textToSVG.getPath(text.text, options)

    const mw = pathData.box.x2 - pathData.box.x1
    const mh = pathData.box.y2 - pathData.box.y1
    const scale = this.size / mw
    const height = scale * mh
    const y = this.y + this.size - height - pathData.box.y1 * scale
    const data = `<g transform="translate(${
      this.x - pathData.box.x1 * scale
    },${y}) scale(${scale})">${pathData.path}</g>`
    return { data, height }
  }

  applyCustomImage(imageData: string): void {
    const eyeImg = fcImageAddSize(imageData, this.x, this.y, this.size, this.size, '')
    this.svgDOM += eyeImg
  }

  applyCustomImageInner(imageData: string, innerShapeWidth: number): void {
    const eyeImg = fcImageAddSize(
      imageData,
      this.x + this.size / 2 - innerShapeWidth / 2,
      this.y + this.size / 2 - innerShapeWidth / 2,
      innerShapeWidth,
      innerShapeWidth,
      ''
    )
    this.svgDOM += eyeImg
  }
}
